import { Controller } from "@hotwired/stimulus"

// Open/close modal with Turbo-compatible lifecycle.
// Focus trapping: Tab cycles within modal.
// Escape key closes, backdrop click closes.
// ARIA: role="dialog", aria-modal="true", focus restoration on close.

export default class extends Controller {
  static targets = ["dialog"]

  connect() {
    this._escapeHandler = this._handleEscape.bind(this)
    this._previousFocus = null
  }

  disconnect() {
    this._removeKeyHandler()
  }

  open() {
    if (!this.hasDialogTarget) return

    this._previousFocus = document.activeElement
    this.dialogTarget.classList.add("modal-open")
    this.dialogTarget.setAttribute("aria-modal", "true")

    document.addEventListener("keydown", this._escapeHandler)

    // Focus first focusable element
    requestAnimationFrame(() => {
      const focusable = this._focusableElements()
      if (focusable.length > 0) focusable[0].focus()
    })
  }

  close() {
    if (!this.hasDialogTarget) return

    this.dialogTarget.classList.remove("modal-open")
    this.dialogTarget.removeAttribute("aria-modal")
    this._removeKeyHandler()

    // Restore focus
    if (this._previousFocus) {
      this._previousFocus.focus()
      this._previousFocus = null
    }
  }

  backdropClose(event) {
    // Only close if clicking the backdrop itself, not the modal content
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  _handleEscape(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    }

    // Focus trapping
    if (event.key === "Tab") {
      const focusable = this._focusableElements()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
  }

  _focusableElements() {
    if (!this.hasDialogTarget) return []
    return Array.from(
      this.dialogTarget.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    )
  }

  _removeKeyHandler() {
    document.removeEventListener("keydown", this._escapeHandler)
  }
}
